import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, FileText, Download, Search as SearchIcon, Edit, X, Save, Trash2, Copy } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import { recalcAll, getDefaultHsn, getEffectiveGstRate } from '@/lib/gstHelper';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ProformaInvoiceReports = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [bills, setBills] = useState([]);
  const [editingBill, setEditingBill] = useState(null);
  const [originalBillNo, setOriginalBillNo] = useState(null);
  const [sortColumn, setSortColumn] = useState('');
  const [sortDirection, setSortDirection] = useState('asc');
  const [userRole, setUserRole] = useState(null);
  const [searchCriteria, setSearchCriteria] = useState({
    fy: '2026-27',
    magazine: '',
    bill_no: '',
    entity_id: '',
    company_name: '',
    type: ''
  });

  // Get current user role
  useEffect(() => {
    const role = localStorage.getItem('role');
    setUserRole(role);
  }, []);

  const handleSearch = async (e) => {
    if (e) e.preventDefault();
    setLoading(true);
    
    try {
      // Add document_type filter to only get Proforma Invoices
      const searchParams = {
        ...searchCriteria,
        document_type: 'Proforma Invoice'
      };
      
      const response = await axios.post(`${API}/bills/search`, searchParams);
      
      // Sort by proforma_invoice_no (largest to smallest) - extract numeric part
      const sortedBills = response.data.sort((a, b) => {
        const extractNumber = (piNo) => {
          const match = String(piNo).match(/\d+/);
          return match ? parseInt(match[0]) : 0;
        };
        return extractNumber(b.proforma_invoice_no) - extractNumber(a.proforma_invoice_no);
      });
      
      setBills(sortedBills);
      
      if (sortedBills.length === 0) {
        toast.info('No Proforma Invoices found matching the criteria');
      } else {
        toast.success(`Found ${sortedBills.length} Proforma Invoice(s)`);
      }
    } catch (error) {
      console.error('Error searching Proforma Invoices:', error);
      toast.error('Failed to search Proforma Invoices');
    } finally {
      setLoading(false);
    }
  };

  const handleSort = (column) => {
    if (sortColumn === column) {
      if (sortDirection === 'asc') {
        setSortDirection('desc');
      } else if (sortDirection === 'desc') {
        setSortColumn('');
        setSortDirection('asc');
      }
    } else {
      setSortColumn(column);
      setSortDirection('asc');
    }
  };

  const getSortedBills = () => {
    if (!sortColumn) return bills;

    return [...bills].sort((a, b) => {
      let aVal = a[sortColumn] || '';
      let bVal = b[sortColumn] || '';

      // Handle date columns
      if (sortColumn === 'created_at') {
        aVal = aVal ? new Date(aVal).getTime() : 0;
        bVal = bVal ? new Date(bVal).getTime() : 0;
      }
      // Handle numeric columns
      else if (sortColumn === 'gross_amount' || sortColumn === 'total_amount') {
        aVal = parseFloat(aVal) || 0;
        bVal = parseFloat(bVal) || 0;
      }
      // Handle string columns
      else if (typeof aVal === 'string') {
        aVal = aVal.toLowerCase();
        bVal = bVal.toLowerCase();
      }

      if (aVal < bVal) return sortDirection === 'asc' ? -1 : 1;
      if (aVal > bVal) return sortDirection === 'asc' ? 1 : -1;
      return 0;
    });
  };

  const handleReset = () => {
    setSearchCriteria({
      fy: '2026-27',
      magazine: '',
      bill_no: '',
      entity_id: '',
      company_name: '',
      type: ''
    });
    setBills([]);
    setSortColumn('');
    setSortDirection('asc');
  };

  // Auto-compute GST + total via shared gstHelper.recalcAll.
  // Honors gst_rate override and last_edited_field ('total' triggers reverse calc).
  const recalcGSTAndTotal = (state) => {
    const n = recalcAll({
      ...state,
      gross_amount: parseFloat(state.gross_amount) || 0,
      discount_amount: parseFloat(state.discount_amount) || 0,
      total_amount: parseFloat(state.total_amount) || 0,
    });
    return {
      gross_amount: state.last_edited_field === 'total' ? (n.gross_amount ?? state.gross_amount) : state.gross_amount,
      sgst_amount: n.sgst_amount,
      cgst_amount: n.cgst_amount,
      igst_amount: n.igst_amount,
      total_amount: n.total_amount,
    };
  };

  const handleEdit = (bill) => {
    try {
      console.log('Opening edit modal for PI:', bill);
      
      // Default detailed terms if stored terms are short or empty
      const defaultDetailedTerms = '1. Artwork approval required before printing for multiple issues.\n2. Post-order confirmation, PO with full terms should be provided.\n3. Dimensions: Full Page Size of 7.25" x 10.5" (A4)\n4. Supported file formats: .jpeg, .jpg, .pdf, .cdr (CDR v.17 or below)\n5. 1 print copy shall be sent at your registered postal address.';
      
      // Use stored terms if they have more than 100 characters (i.e., detailed terms), else use default
      const termsToShow = (bill.terms_conditions && bill.terms_conditions.length > 100) 
        ? bill.terms_conditions 
        : defaultDetailedTerms;
      
      // Generate item description if not stored - same logic as PrintBill.js
      const generateItemDescription = () => {
        const product = bill.product === 'Renewal' ? 'Subscription Renewal' : bill.product || 'Subscription';
        const tenure = bill.period_duration || bill.advertisement_tenure || '1 Month';
        const occasion = bill.occasion && bill.occasion !== 'None' ? ` in occasion ${bill.occasion}` : '';
        return `Towards your ${product} charges of ${bill.magazine} magazine for ${tenure}${occasion}\nHSN Code: 998363`;
      };
      
      // Use stored item_description if exists, else generate it
      const itemDescToShow = bill.item_description || generateItemDescription();
      
      setEditingBill({ 
        ...bill,
        fy: bill.fy || '2024-25',
        document_type: 'Proforma Invoice',
        magazine: bill.magazine || '',
        type: bill.type || '',
        product: bill.product || '',
        initial: bill.initial || 'M/s.',
        billing_state: bill.billing_state || '',
        occasion: bill.occasion || '',
        bill_date: bill.bill_date || new Date().toISOString().split('T')[0],
        po_wo_no: bill.po_wo_no || '',
        proforma_invoice_no: bill.proforma_invoice_no || '',
        entity_id: bill.entity_id || '',
        billing_name: bill.billing_name || '',
        bill_receipt_name: bill.bill_receipt_name || '',
        billing_address: bill.billing_address || '',
        district: bill.district || '',
        pincode: bill.pincode || '',
        gst_no: bill.gst_no || '',
        start_month: bill.start_month || '',
        start_year: bill.start_year || '',
        end_month: bill.end_month || '',
        end_year: bill.end_year || '',
        advertisement_position: bill.advertisement_position || '',
        advertisement_tenure: bill.advertisement_tenure || '',
        terms_conditions: termsToShow,
        item_description: itemDescToShow,
        gross_amount: bill.gross_amount || 0,
        discount_percentage: bill.discount_percentage || '',
        discount_amount: bill.discount_amount || '',
        sgst_amount: bill.sgst_amount || 0,
        cgst_amount: bill.cgst_amount || 0,
        igst_amount: bill.igst_amount || 0,
        total_amount: bill.total_amount || 0,
        gst_rate: bill.gst_rate || 'auto',
        hsn_sac: bill.hsn_sac || getDefaultHsn(bill.product || ''),
        last_edited_field: '',
      });
      setOriginalBillNo(bill.bill_no);
      console.log('Edit modal state set successfully');
    } catch (error) {
      console.error('Error in handleEdit:', error);
      toast.error('Failed to open edit modal: ' + error.message);
    }
  };

  const handleCancelEdit = () => {
    setEditingBill(null);
    setOriginalBillNo(null);
  };

  const handleSaveEdit = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      
      let billDateToSave = editingBill.bill_date || '';
      if (billDateToSave && !billDateToSave.match(/^\d{4}-\d{2}-\d{2}$/)) {
        const parts = billDateToSave.split('/');
        if (parts.length === 3) {
          billDateToSave = `${parts[2]}-${parts[1]}-${parts[0]}`;
        }
      }

      const updateData = {
        fy: editingBill.fy,
        document_type: 'Proforma Invoice',
        magazine: editingBill.magazine,
        type: editingBill.type,
        product: editingBill.product,
        po_wo_no: editingBill.po_wo_no,
        proforma_invoice_no: editingBill.proforma_invoice_no,
        entity_id: editingBill.entity_id,
        initial: editingBill.initial,
        billing_name: editingBill.billing_name,
        bill_receipt_name: editingBill.bill_receipt_name,
        billing_address: editingBill.billing_address || '',
        billing_state: editingBill.billing_state,
        district: editingBill.district || '',
        pincode: editingBill.pincode || '',
        gst_no: editingBill.gst_no || '',
        occasion: editingBill.occasion,
        bill_no: editingBill.bill_no || '',
        bill_date: billDateToSave,
        start_month: editingBill.start_month || '',
        start_year: editingBill.start_year || '',
        end_month: editingBill.end_month || '',
        end_year: editingBill.end_year || '',
        advertisement_position: editingBill.advertisement_position || '',
        advertisement_tenure: editingBill.advertisement_tenure || '',
        item_description: editingBill.item_description || '',
        include_in_aabkari: editingBill.include_in_aabkari || false,
        gross_amount: parseFloat(editingBill.gross_amount) || 0,
        discount_percentage: parseFloat(editingBill.discount_percentage) || 0,
        discount_amount: parseFloat(editingBill.discount_amount) || 0,
        sgst_amount: parseFloat(editingBill.sgst_amount) || 0,
        cgst_amount: parseFloat(editingBill.cgst_amount) || 0,
        igst_amount: parseFloat(editingBill.igst_amount) || 0,
        total_amount: parseFloat(editingBill.total_amount) || (parseFloat(editingBill.gross_amount) || 0) - (parseFloat(editingBill.discount_amount) || 0),
        gst_rate: editingBill.gst_rate || 'auto',
        hsn_sac: editingBill.hsn_sac || '',
        terms_conditions: editingBill.terms_conditions || ''
      };
      
      await axios.put(`${API}/bills/${originalBillNo}`, updateData, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      toast.success('Proforma Invoice updated successfully');
      setEditingBill(null);
      setOriginalBillNo(null);
      handleSearch({ preventDefault: () => {} });
    } catch (error) {
      console.error('Error updating PI:', error);
      toast.error('Failed to update: ' + (error.response?.data?.detail || error.message));
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (billNo) => {
    if (!window.confirm(`Are you sure you want to delete this Proforma Invoice?`)) {
      return;
    }

    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/bills/${billNo}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      toast.success('Proforma Invoice deleted successfully');
      handleSearch({ preventDefault: () => {} });
    } catch (error) {
      console.error('Error deleting PI:', error);
      toast.error('Failed to delete Proforma Invoice');
    } finally {
      setLoading(false);
    }
  };

  const handlePrint = () => {
    // Set document title for PDF filename
    document.title = 'Proforma Invoice Report';
    window.print();
    // Reset title after print
    setTimeout(() => { document.title = 'Magazine CRM'; }, 1000);
  };

  const exportToCSV = () => {
    if (bills.length === 0) {
      toast.error('No data to export');
      return;
    }

    try {
      const sortedBills = getSortedBills();
      
      const headers = ['PI No', 'Magazine', 'Type', 'Billing Name', 'Billing State', 'GST No', 'Gross Amount', 'Total Amount', 'Created Date'];
      const csvContent = [
        headers.join(','),
        ...sortedBills.map(bill => {
          return [
            bill.proforma_invoice_no || '',
            bill.magazine || '',
            bill.type || '',
            `"${bill.billing_name || ''}"`,
            bill.billing_state || '',
            bill.gst_no || '',
            bill.gross_amount || 0,
            bill.total_amount || 0,
            bill.created_at ? new Date(bill.created_at).toLocaleString('en-IN') : ''
          ].join(',');
        })
      ].join('\n');

      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      
      link.setAttribute('href', url);
      link.setAttribute('download', `Proforma_Invoice_Report_${new Date().toISOString().split('T')[0]}.csv`);
      link.style.visibility = 'hidden';
      
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      URL.revokeObjectURL(url);
      
      toast.success('Report downloaded! Check your Downloads folder');
    } catch (error) {
      console.error('Export error:', error);
      toast.error('Failed to export report');
    }
  };

  const months = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];

  return (
    <div className="min-h-screen bg-gradient-to-br from-green-50 via-white to-green-50 py-8">
      <div className="max-w-7xl mx-auto px-6">
        <div className="flex items-center justify-between mb-8 no-print">
          <div className="flex items-center space-x-4">
            <Button variant="outline" onClick={() => navigate('/')} data-testid="back-button">
              <ArrowLeft className="mr-2" size={18} />
              Back
            </Button>
            <h1 className="text-4xl font-bold" style={{ color: '#059669' }} data-testid="reports-title">
              Proforma Invoice Reports
            </h1>
          </div>
          {bills.length > 0 && (
            <div className="flex space-x-2">
              <Button onClick={exportToCSV} className="btn-secondary" data-testid="export-button">
                <Download className="mr-2" size={18} />
                Export to Excel
              </Button>
              <Button onClick={handlePrint} className="btn-primary" style={{ backgroundColor: '#059669' }} data-testid="print-button">
                <FileText className="mr-2" size={18} />
                Print Report
              </Button>
            </div>
          )}
        </div>

        {/* Search Form */}
        <div className="form-section mb-8 no-print">
          <form onSubmit={handleSearch} className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-6 gap-4">
            <div className="space-y-2">
              <Label>Financial Year</Label>
              <Select value={searchCriteria.fy} onValueChange={(value) => setSearchCriteria({ ...searchCriteria, fy: value })}>
                <SelectTrigger data-testid="fy-filter">
                  <SelectValue placeholder="Select FY" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="2026-27">2026-27</SelectItem>
                  <SelectItem value="2025-26">2025-26</SelectItem>
                  <SelectItem value="2024-25">2024-25</SelectItem>
                  <SelectItem value="2023-24">2023-24</SelectItem>
                  <SelectItem value="2022-23">2022-23</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Magazine</Label>
              <Select value={searchCriteria.magazine} onValueChange={(value) => setSearchCriteria({ ...searchCriteria, magazine: value })}>
                <SelectTrigger data-testid="magazine-filter">
                  <SelectValue placeholder="All Magazines" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all_magazines">All Magazines</SelectItem>
                  <SelectItem value="Aabkari Times">Aabkari Times</SelectItem>
                  <SelectItem value="Aabkari Times ENG">Aabkari Times ENG</SelectItem>
                  <SelectItem value="Sugar Times">Sugar Times</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Type</Label>
              <Select value={searchCriteria.type} onValueChange={(value) => setSearchCriteria({ ...searchCriteria, type: value })}>
                <SelectTrigger data-testid="type-filter">
                  <SelectValue placeholder="All Types" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all_types">All Types</SelectItem>
                  <SelectItem value="Member">Member</SelectItem>
                  <SelectItem value="Advertiser">Advertiser</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>PI No</Label>
              <Input
                placeholder="Search PI No"
                value={searchCriteria.bill_no}
                onChange={(e) => setSearchCriteria({ ...searchCriteria, bill_no: e.target.value })}
                data-testid="pi-no-search"
              />
            </div>

            <div className="space-y-2">
              <Label>Company Name</Label>
              <Input
                placeholder="Search company"
                value={searchCriteria.company_name}
                onChange={(e) => setSearchCriteria({ ...searchCriteria, company_name: e.target.value })}
                data-testid="company-search"
              />
            </div>

            <div className="flex items-end space-x-2">
              <Button type="submit" disabled={loading} className="flex-1" style={{ backgroundColor: '#059669' }}>
                <SearchIcon className="mr-2" size={18} />
                {loading ? 'Searching...' : 'Search'}
              </Button>
              <Button type="button" variant="outline" onClick={handleReset}>
                Reset
              </Button>
            </div>
          </form>
        </div>

        {/* Results Table */}
        {bills.length > 0 && (
          <div className="form-section overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b" style={{ backgroundColor: '#d1fae5' }}>
                  <th className="px-4 py-3 text-left cursor-pointer hover:bg-green-100" onClick={() => handleSort('proforma_invoice_no')}>
                    PI No {sortColumn === 'proforma_invoice_no' && (sortDirection === 'asc' ? '↑' : '↓')}
                  </th>
                  <th className="px-4 py-3 text-left cursor-pointer hover:bg-green-100" onClick={() => handleSort('magazine')}>
                    Magazine {sortColumn === 'magazine' && (sortDirection === 'asc' ? '↑' : '↓')}
                  </th>
                  <th className="px-4 py-3 text-left cursor-pointer hover:bg-green-100" onClick={() => handleSort('type')}>
                    Type {sortColumn === 'type' && (sortDirection === 'asc' ? '↑' : '↓')}
                  </th>
                  <th className="px-4 py-3 text-left cursor-pointer hover:bg-green-100" onClick={() => handleSort('billing_name')}>
                    Billing Name {sortColumn === 'billing_name' && (sortDirection === 'asc' ? '↑' : '↓')}
                  </th>
                  <th className="px-4 py-3 text-left">State</th>
                  <th className="px-4 py-3 text-right cursor-pointer hover:bg-green-100" onClick={() => handleSort('gross_amount')}>
                    Gross Amt {sortColumn === 'gross_amount' && (sortDirection === 'asc' ? '↑' : '↓')}
                  </th>
                  <th className="px-4 py-3 text-right cursor-pointer hover:bg-green-100" onClick={() => handleSort('total_amount')}>
                    Total Amt {sortColumn === 'total_amount' && (sortDirection === 'asc' ? '↑' : '↓')}
                  </th>
                  <th className="px-4 py-3 text-center no-print">Actions</th>
                </tr>
              </thead>
              <tbody>
                {getSortedBills().map((bill, index) => (
                  <tr key={index} className="border-b hover:bg-green-50">
                    <td className="px-4 py-3 font-semibold" style={{ color: '#059669' }}>
                      {bill.proforma_invoice_no || bill.bill_no}
                    </td>
                    <td className="px-4 py-3">{bill.magazine}</td>
                    <td className="px-4 py-3">{bill.type}</td>
                    <td className="px-4 py-3">{bill.billing_name}</td>
                    <td className="px-4 py-3">{bill.billing_state}</td>
                    <td className="px-4 py-3 text-right">₹{(bill.gross_amount || 0).toLocaleString('en-IN')}</td>
                    <td className="px-4 py-3 text-right font-semibold">₹{(bill.total_amount || 0).toLocaleString('en-IN')}</td>
                    <td className="px-4 py-3 text-center no-print">
                      <div className="flex justify-center space-x-2">
                        <Button size="sm" variant="outline" onClick={() => handleEdit(bill)} title="Edit">
                          <Edit size={14} />
                        </Button>
                        <Button size="sm" variant="outline" onClick={() => {
                          const copyData = { ...bill };
                          delete copyData.bill_no;
                          delete copyData.proforma_invoice_no;
                          delete copyData.id;
                          delete copyData.created_at;
                          delete copyData.updated_at;
                          navigate('/create-proforma-invoice', { state: { copyFrom: copyData } });
                        }} title="Duplicate" className="text-blue-600 border-blue-300 hover:bg-blue-50">
                          <Copy size={14} />
                        </Button>
                        <Button 
                          size="sm" 
                          variant="outline" 
                          onClick={() => navigate(`/print-bill?bill_no=${bill.bill_no}&magazine=${bill.magazine}`)}
                          title="Print"
                        >
                          <FileText size={14} />
                        </Button>
                        {userRole === 'admin' && (
                          <Button size="sm" variant="destructive" onClick={() => handleDelete(bill.bill_no)} title="Delete">
                            <Trash2 size={14} />
                          </Button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr className="border-t-2 font-bold" style={{ backgroundColor: '#d1fae5' }}>
                  <td colSpan="5" className="px-4 py-3 text-right">Total:</td>
                  <td className="px-4 py-3 text-right">
                    ₹{bills.reduce((sum, b) => sum + (b.gross_amount || 0), 0).toLocaleString('en-IN')}
                  </td>
                  <td className="px-4 py-3 text-right">
                    ₹{bills.reduce((sum, b) => sum + (b.total_amount || 0), 0).toLocaleString('en-IN')}
                  </td>
                  <td className="no-print"></td>
                </tr>
              </tfoot>
            </table>
          </div>
        )}

        {/* Edit Modal */}
        {editingBill && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto p-6">
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-2xl font-bold" style={{ color: '#059669' }}>Edit Proforma Invoice</h2>
                <Button variant="ghost" onClick={handleCancelEdit}>
                  <X size={24} />
                </Button>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label>PI No *</Label>
                  <Input
                    value={editingBill.proforma_invoice_no}
                    onChange={(e) => setEditingBill({ ...editingBill, proforma_invoice_no: e.target.value })}
                  />
                </div>

                <div className="space-y-2">
                  <Label>FY</Label>
                  <Select value={editingBill.fy || '2026-27'} onValueChange={(value) => setEditingBill({ ...editingBill, fy: value })}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="2024-25">2024-25</SelectItem>
                      <SelectItem value="2025-26">2025-26</SelectItem>
                      <SelectItem value="2026-27">2026-27</SelectItem>
                      <SelectItem value="2027-28">2027-28</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label>Magazine</Label>
                  <Select value={editingBill.magazine} onValueChange={(value) => setEditingBill({ ...editingBill, magazine: value })}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select magazine" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Aabkari Times">Aabkari Times</SelectItem>
                  <SelectItem value="Aabkari Times ENG">Aabkari Times ENG</SelectItem>
                      <SelectItem value="Sugar Times">Sugar Times</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label>Type</Label>
                  <Select value={editingBill.type} onValueChange={(value) => setEditingBill({ ...editingBill, type: value })}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select type" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Member">Member</SelectItem>
                      <SelectItem value="Advertiser">Advertiser</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label>Product</Label>
                  <Select value={editingBill.product || ''} onValueChange={(value) => setEditingBill({ ...editingBill, product: value })}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select product" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Subscription">Subscription</SelectItem>
                      <SelectItem value="Renewal">Renewal</SelectItem>
                      <SelectItem value="Advertisement">Advertisement</SelectItem>
                      <SelectItem value="Subscription/Advertisement">Subscription/Advertisement</SelectItem>
                      <SelectItem value="Event">Event</SelectItem>
                      <SelectItem value="Other">Other</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label>Date (DD/MM/YYYY)</Label>
                  <Input
                    value={editingBill.bill_date}
                    onChange={(e) => setEditingBill({ ...editingBill, bill_date: e.target.value })}
                    placeholder="DD/MM/YYYY"
                  />
                </div>

                <div className="space-y-2">
                  <Label>Entity ID</Label>
                  <Input
                    value={editingBill.entity_id || ''}
                    onChange={(e) => setEditingBill({ ...editingBill, entity_id: e.target.value })}
                  />
                </div>

                <div className="space-y-2">
                  <Label>Initial</Label>
                  <Select value={editingBill.initial} onValueChange={(value) => setEditingBill({ ...editingBill, initial: value })}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Mr.">Mr.</SelectItem>
                      <SelectItem value="Mrs.">Mrs.</SelectItem>
                      <SelectItem value="Ms.">Ms.</SelectItem>
                      <SelectItem value="Dr.">Dr.</SelectItem>
                      <SelectItem value="M/s.">M/s.</SelectItem>
                      <SelectItem value="The">The</SelectItem>
                      <SelectItem value="To">To</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label>Billing Name *</Label>
                  <Input
                    value={editingBill.billing_name}
                    onChange={(e) => setEditingBill({ ...editingBill, billing_name: e.target.value })}
                  />
                </div>

                <div className="space-y-2 md:col-span-2">
                  <Label>Billing Address</Label>
                  <Textarea
                    value={editingBill.billing_address}
                    onChange={(e) => setEditingBill({ ...editingBill, billing_address: e.target.value })}
                    rows={2}
                  />
                </div>

                <div className="space-y-2">
                  <Label>State</Label>
                  <Input
                    value={editingBill.billing_state}
                    onChange={(e) => setEditingBill({ ...editingBill, billing_state: e.target.value })}
                  />
                </div>

                <div className="space-y-2">
                  <Label>District</Label>
                  <Input
                    value={editingBill.district || ''}
                    onChange={(e) => setEditingBill({ ...editingBill, district: e.target.value })}
                  />
                </div>

                <div className="space-y-2">
                  <Label>Pincode</Label>
                  <Input
                    value={editingBill.pincode || ''}
                    onChange={(e) => setEditingBill({ ...editingBill, pincode: e.target.value })}
                  />
                </div>

                <div className="space-y-2">
                  <Label>GST No.</Label>
                  <Input
                    value={editingBill.gst_no || ''}
                    onChange={(e) => setEditingBill({ ...editingBill, gst_no: e.target.value })}
                  />
                </div>

                <div className="space-y-2">
                  <Label>P.O./W.O. No.</Label>
                  <Input
                    value={editingBill.po_wo_no || ''}
                    onChange={(e) => setEditingBill({ ...editingBill, po_wo_no: e.target.value })}
                  />
                </div>

                <div className="space-y-2">
                  <Label>Occasion</Label>
                  <Input
                    value={editingBill.occasion || ''}
                    onChange={(e) => setEditingBill({ ...editingBill, occasion: e.target.value })}
                  />
                </div>

                <div className="space-y-2">
                  <Label>Start Month</Label>
                  <Select value={editingBill.start_month || ''} onValueChange={(value) => setEditingBill({ ...editingBill, start_month: value })}>
                    <SelectTrigger><SelectValue placeholder="Select month" /></SelectTrigger>
                    <SelectContent>
                      {['January','February','March','April','May','June','July','August','September','October','November','December'].map(m => (
                        <SelectItem key={m} value={m}>{m}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label>Start Year</Label>
                  <Input
                    value={editingBill.start_year || ''}
                    onChange={(e) => setEditingBill({ ...editingBill, start_year: e.target.value })}
                    placeholder="e.g. 2026"
                  />
                </div>

                <div className="space-y-2">
                  <Label>End Month</Label>
                  <Select value={editingBill.end_month || ''} onValueChange={(value) => setEditingBill({ ...editingBill, end_month: value })}>
                    <SelectTrigger><SelectValue placeholder="Select month" /></SelectTrigger>
                    <SelectContent>
                      {['January','February','March','April','May','June','July','August','September','October','November','December'].map(m => (
                        <SelectItem key={m} value={m}>{m}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label>End Year</Label>
                  <Input
                    value={editingBill.end_year || ''}
                    onChange={(e) => setEditingBill({ ...editingBill, end_year: e.target.value })}
                    placeholder="e.g. 2027"
                  />
                </div>

                <div className="space-y-2">
                  <Label>Ad Position</Label>
                  <Input
                    value={editingBill.advertisement_position || ''}
                    onChange={(e) => setEditingBill({ ...editingBill, advertisement_position: e.target.value })}
                    placeholder="e.g. Full Page"
                  />
                </div>

                <div className="space-y-2">
                  <Label>Ad Tenure</Label>
                  <Input
                    value={editingBill.advertisement_tenure || ''}
                    onChange={(e) => setEditingBill({ ...editingBill, advertisement_tenure: e.target.value })}
                    placeholder="e.g. 12 Issues"
                  />
                </div>

                {/* Include in Aabkari Times (complimentary) — only for Sugar Times + Advertiser */}
                {editingBill.magazine === 'Sugar Times' && editingBill.type === 'Advertiser' && (
                  <div className="md:col-span-3">
                    <div className="flex items-center space-x-2 bg-yellow-50 p-3 rounded-lg border border-yellow-200">
                      <input
                        type="checkbox"
                        id="pi_edit_include_in_aabkari"
                        checked={!!editingBill.include_in_aabkari}
                        onChange={(e) => setEditingBill({ ...editingBill, include_in_aabkari: e.target.checked })}
                        className="w-4 h-4 cursor-pointer"
                        data-testid="pi-edit-include-in-aabkari"
                      />
                      <Label htmlFor="pi_edit_include_in_aabkari" className="cursor-pointer font-semibold">
                        Include advertisement in Aabkari Times also (Complimentary Offer)
                      </Label>
                    </div>
                  </div>
                )}

                <div className="space-y-2">
                  <Label>GST Rate</Label>
                  <Select
                    value={editingBill.gst_rate || 'auto'}
                    onValueChange={(value) => {
                      const next = { ...editingBill, gst_rate: value };
                      Object.assign(next, recalcGSTAndTotal(next));
                      setEditingBill(next);
                    }}
                  >
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="auto">Auto (by product)</SelectItem>
                      <SelectItem value="0">0% (No GST)</SelectItem>
                      <SelectItem value="5">5%</SelectItem>
                      <SelectItem value="18">18%</SelectItem>
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-gray-500">Effective: {(getEffectiveGstRate(editingBill) * 100).toFixed(0)}%</p>
                </div>

                <div className="space-y-2">
                  <Label>HSN / SAC</Label>
                  <Input
                    value={editingBill.hsn_sac || ''}
                    onChange={(e) => setEditingBill({ ...editingBill, hsn_sac: e.target.value })}
                    placeholder="998363 / 998397"
                  />
                </div>

                <div className="space-y-2">
                  <Label>Gross Amount *</Label>
                  <Input
                    type="number"
                    step="0.01"
                    value={editingBill.gross_amount}
                    onChange={(e) => {
                      const gross = parseFloat(e.target.value) || 0;
                      const next = { ...editingBill, gross_amount: gross, last_edited_field: 'gross' };
                      const pct = parseFloat(editingBill.discount_percentage) || 0;
                      if (pct > 0) next.discount_amount = Math.round(gross * pct / 100 * 100) / 100;
                      Object.assign(next, recalcGSTAndTotal(next));
                      setEditingBill(next);
                    }}
                  />
                </div>

                <div className="space-y-2">
                  <Label>Discount %</Label>
                  <Input
                    type="number"
                    step="0.01"
                    value={editingBill.discount_percentage || ''}
                    onChange={(e) => {
                      const pct = parseFloat(e.target.value) || 0;
                      const gross = parseFloat(editingBill.gross_amount) || 0;
                      const discAmt = Math.round(gross * pct / 100 * 100) / 100;
                      const next = { ...editingBill, discount_percentage: pct, discount_amount: discAmt, last_edited_field: 'discount' };
                      Object.assign(next, recalcGSTAndTotal(next));
                      setEditingBill(next);
                    }}
                    placeholder="e.g. 20"
                  />
                </div>

                <div className="space-y-2">
                  <Label>Discount Amount</Label>
                  <Input
                    type="number"
                    step="0.01"
                    value={editingBill.discount_amount || ''}
                    onChange={(e) => {
                      const discAmt = parseFloat(e.target.value) || 0;
                      const gross = parseFloat(editingBill.gross_amount) || 0;
                      const pct = gross > 0 ? Math.round(discAmt / gross * 100 * 100) / 100 : 0;
                      const next = { ...editingBill, discount_amount: discAmt, discount_percentage: pct, last_edited_field: 'discount' };
                      Object.assign(next, recalcGSTAndTotal(next));
                      setEditingBill(next);
                    }}
                    className="bg-gray-50"
                  />
                </div>

                {getEffectiveGstRate(editingBill) > 0 && (
                  <>
                    <div className="space-y-2">
                      <Label>SGST</Label>
                      <Input type="number" value={Number(editingBill.sgst_amount || 0).toFixed(2)} disabled className="bg-gray-100" />
                    </div>
                    <div className="space-y-2">
                      <Label>CGST</Label>
                      <Input type="number" value={Number(editingBill.cgst_amount || 0).toFixed(2)} disabled className="bg-gray-100" />
                    </div>
                    <div className="space-y-2">
                      <Label>IGST</Label>
                      <Input type="number" value={Number(editingBill.igst_amount || 0).toFixed(2)} disabled className="bg-gray-100" />
                    </div>
                  </>
                )}

                <div className="space-y-2 md:col-span-3">
                  <Label className="font-bold text-green-700">Grand Total (Inclusive of GST)</Label>
                  <Input
                    type="number"
                    step="0.01"
                    value={editingBill.total_amount}
                    onChange={(e) => {
                      const next = { ...editingBill, total_amount: parseFloat(e.target.value) || 0, last_edited_field: 'total' };
                      Object.assign(next, recalcGSTAndTotal(next));
                      setEditingBill(next);
                    }}
                    className="bg-green-50 font-bold text-lg border-green-300"
                  />
                  <p className="text-xs text-gray-500">Type here for GST-inclusive: Taxable = Total / (1 + Rate). Gross auto-reverses.</p>
                </div>

                <div className="space-y-2 md:col-span-3">
                  <Label>Item Description (shown in printed PI)</Label>
                  <Textarea
                    value={editingBill.item_description || ''}
                    onChange={(e) => setEditingBill({ ...editingBill, item_description: e.target.value })}
                    rows={3}
                  />
                </div>

                <div className="space-y-2 md:col-span-3">
                  <Label>Terms & Conditions</Label>
                  <Textarea
                    value={editingBill.terms_conditions}
                    onChange={(e) => setEditingBill({ ...editingBill, terms_conditions: e.target.value })}
                    rows={5}
                  />
                </div>
              </div>

              <div className="flex justify-end space-x-4 mt-6">
                <Button variant="outline" onClick={handleCancelEdit}>
                  Cancel
                </Button>
                <Button onClick={handleSaveEdit} disabled={loading} style={{ backgroundColor: '#059669' }}>
                  <Save className="mr-2" size={18} />
                  {loading ? 'Saving...' : 'Save Changes'}
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ProformaInvoiceReports;
